import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { fetchSharePointEvents } from "../utils/SharePointService";
import "./CalendarPage.css";

const CalendarPage = () => {
  const [activeTab, setActiveTab] = useState("exam");
  const [viewMode, setViewMode] = useState("week");
  const [selectedDate, setSelectedDate] = useState(moment());
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [eventsFromFile, setEventsFromFile] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load events from SharePoint or fallback to CSV file
  useEffect(() => {
    const loadEventsFromFile = async () => {
      setIsLoading(true);
      
      try {
        // SharePoint List URL
        const sharePointUrl = "https://myoffice.accenture.com/:l:/r/personal/saranya_a_shanmugam_accenture_com/Lists/QRP_Program_Schedule?e=S9N7I7";
        
        console.log("========================================");
        console.log("Attempting to fetch from SharePoint...");
        console.log("========================================");
        
        // Try to fetch from SharePoint without MSAL
        const sharePointEvents = await fetchSharePointEvents(sharePointUrl);
        
        if (sharePointEvents && sharePointEvents.length > 0) {
          console.log("✅ Successfully loaded events from SharePoint:", sharePointEvents.length, "events");
          setEventsFromFile(sharePointEvents);
          setIsLoading(false);
          return;
        }
        
        console.log("⚠️ No SharePoint events found, falling back to CSV...");
        throw new Error("No events from SharePoint");
        
      } catch (error) {
        console.error("❌ SharePoint fetch failed:", error.message);
        console.log("📁 Loading from CSV file...");
        
        // Fallback to CSV file if SharePoint fails
        try {
          const response = await fetch(process.env.PUBLIC_URL + "/calendar-events.csv");
          const text = await response.text();
          
          // Parse CSV manually
          const lines = text.split("\n");
          const events = [];
          
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              const values = lines[i].split(",");
              
              const event = {
                id: parseInt(values[0]),
                title: values[1],
                start: moment(`${values[2]} ${values[3]}`, "YYYY-MM-DD HH:mm"),
                end: moment(`${values[4]} ${values[5]}`, "YYYY-MM-DD HH:mm"),
                category: values[6],
                type: values[7].trim()
              };
              
              events.push(event);
            }
          }
          
          console.log("✅ Loaded events from CSV:", events.length, "events");
          setEventsFromFile(events);
        } catch (csvError) {
          console.error("❌ CSV fallback also failed:", csvError);
        }
        
        setIsLoading(false);
      }
    };

    loadEventsFromFile();
  }, []);

  // Generate calendar dates dynamically for current month
  const generateCalendarDates = (month) => {
    const startOfMonth = moment(month).startOf("month");
    const endOfMonth = moment(month).endOf("month");
    const startDate = moment(startOfMonth).startOf("week");
    const endDate = moment(endOfMonth).endOf("week");

    const calendar = [];
    let week = [];
    let day = moment(startDate);

    while (day.isSameOrBefore(endDate)) {
      week.push({
        date: day.date(),
        month: day.month(),
        isCurrentMonth: day.month() === month.month(),
        fullDate: moment(day),
      });

      if (week.length === 7) {
        calendar.push(week);
        week = [];
      }
      day.add(1, "day");
    }

    return calendar;
  };

  const calendarDates = useMemo(
    () => generateCalendarDates(currentMonth),
    [currentMonth]
  );

  // Use events from file if loaded, otherwise use empty array
  const allEvents = useMemo(
    () => eventsFromFile,
    [eventsFromFile]
  );

  // Filter events based on active tab
  const events = useMemo(
    () => allEvents.filter((event) => event.category === activeTab),
    [allEvents, activeTab]
  );

  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
  const timeSlots = [
    "8 AM", "9 AM", "10 AM", "11 AM", "12 PM",
    "1 PM", "2 PM", "3 PM", "4 PM", "5 PM", "6 PM"
  ];

  // Get current week dates for week view
  const getCurrentWeekDates = () => {
    const startOfWeek = moment(selectedDate).startOf("week").add(1, "day"); // Start from Monday
    const dates = [];
    for (let i = 0; i < 5; i++) {
      dates.push(moment(startOfWeek).add(i, "days"));
    }
    return dates;
  };

  const weekDates = getCurrentWeekDates();

  // Get events for a specific date and time
  const getEventsForDateTime = (date, time) => {
    const hour = parseInt(time);
    return events.filter((event) => {
      return (
        event.start.isSame(date, "day") &&
        event.start.hour() === hour
      );
    });
  };

  // Get all events for a specific date and check overlap
  const getOverlappingEvents = (date, startHour) => {
    const dayEvents = events.filter((event) => event.start.isSame(date, "day"));
    
    // Find events that overlap with this time slot
    const overlapping = dayEvents.filter((event) => {
      const eventStartHour = event.start.hour();
      const eventEndHour = event.end.hour();
      const eventEndMinutes = event.end.minute();
      
      // Event overlaps if it starts at or before this hour and ends after this hour
      return eventStartHour <= startHour && 
             (eventEndHour > startHour || (eventEndHour === startHour && eventEndMinutes > 0));
    });
    
    return overlapping;
  };

  const handleDateClick = (dateInfo) => {
    if (dateInfo.isCurrentMonth) {
      setSelectedDate(dateInfo.fullDate);
    }
  };

  const handleMonthChange = (e) => {
    const monthIndex = moment().month(e.target.value).month();
    setCurrentMonth(moment(currentMonth).month(monthIndex));
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedEvent(null);
  };

  // Get dates to display based on view mode
  const getDisplayDates = () => {
    if (viewMode === "day") {
      return [moment(selectedDate)];
    } else if (viewMode === "week") {
      const startOfWeek = moment(selectedDate).startOf("week").add(1, "day"); // Monday
      const dates = [];
      for (let i = 0; i < 5; i++) {
        dates.push(moment(startOfWeek).add(i, "days"));
      }
      return dates;
    } else {
      // Month view - return all days in the current month with week structure
      const startOfMonth = moment(currentMonth).startOf("month");
      const endOfMonth = moment(currentMonth).endOf("month");
      const startDate = moment(startOfMonth).startOf("week").add(1, "day"); // Start from Monday
      const endDate = moment(endOfMonth).endOf("week").add(1, "day");
      
      const dates = [];
      let day = moment(startDate);
      
      while (day.isBefore(endDate)) {
        dates.push({
          date: moment(day),
          isCurrentMonth: day.month() === currentMonth.month()
        });
        day.add(1, "day");
      }
      
      return dates;
    }
  };

  // Get all events for a specific date (for month view)
  const getEventsForDate = (date) => {
    return events.filter((event) => event.start.isSame(date, "day"));
  };

  return (
    <div className="calendar-page">
      <section
        className="calendar-hero"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.75) 0%, rgba(0,0,0,.15) 65%, rgba(0,0,0,.15) 100%), url(${
            process.env.PUBLIC_URL + "/Images/HomePage/calendar-banner.png"
          })`,
        }}
      >
        <div className="calendar-hero__inner">
          <h1 className="calendar-hero__title">Cluster & Assessment Registration Calendar</h1>
          <p className="calendar-hero__subtitle">Know when to register, learn, and get assessed</p>
        </div>
      </section>

      <div className="calendar-container">
        <div className="calendar-tabs">
          <button
            className={`tab ${activeTab === "program" ? "active" : ""}`}
            onClick={() => setActiveTab("program")}
          >
            Program Schedule
          </button>
          <button
            className={`tab ${activeTab === "exam" ? "active" : ""}`}
            onClick={() => setActiveTab("exam")}
          >
            Exam Schedule
          </button>
        </div>

        <div className="calendar-content">
          <h3 className="calendar-subtitle">
            Calendar - Post Onboard Learning Program
          </h3>

          <div className="calendar-main">
            <div className="calendar-sidebar">
              <div className="month-selector">
                <div className="month-dropdown-wrapper">
                  <select
                    value={currentMonth.format("MMMM")}
                    onChange={handleMonthChange}
                    className="month-dropdown"
                  >
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                  <option value="July">July</option>
                  <option value="August">August</option>
                  <option value="September">September</option>
                  <option value="October">October</option>
                  <option value="November">November</option>
                  <option value="December">December</option>
                  </select>
                  <span className="dropdown-arrow">▼</span>
                </div>
              </div>

              <div className="calendar-mini">
                <div className="calendar-header">
                  <div className="weekdays">
                    {weekDays.map((day, index) => (
                      <div key={index} className="weekday">
                        {day}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="calendar-grid">
                  {calendarDates.map((week, weekIndex) => (
                    <div key={weekIndex} className="calendar-week">
                      {week.map((dateInfo, dateIndex) => (
                        <div
                          key={dateIndex}
                          className={`calendar-date ${
                            dateInfo.fullDate.isSame(selectedDate, "day")
                              ? "selected"
                              : ""
                          } ${!dateInfo.isCurrentMonth ? "empty" : ""}`}
                          onClick={() => handleDateClick(dateInfo)}
                        >
                          {dateInfo.isCurrentMonth ? dateInfo.date : ""}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="calendar-view">
              <div className="view-controls">
                <button
                  className={`view-btn ${viewMode === "day" ? "active" : ""}`}
                  onClick={() => setViewMode("day")}
                >
                  Day
                </button>
                <button
                  className={`view-btn ${viewMode === "week" ? "active" : ""}`}
                  onClick={() => setViewMode("week")}
                >
                  Week
                </button>
                <button
                  className={`view-btn ${viewMode === "month" ? "active" : ""}`}
                  onClick={() => setViewMode("month")}
                >
                  Month
                </button>
              </div>

              {isLoading ? (
                <div className="loading-message">Loading events...</div>
              ) : viewMode === "month" ? (
                // Month Grid View
                <div className="month-grid">
                  <div className="month-weekdays">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, index) => (
                      <div key={index} className="month-weekday">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="month-days">
                    {getDisplayDates().map((dateInfo, index) => {
                      const date = dateInfo.date;
                      const dayEvents = getEventsForDate(date);
                      return (
                        <div
                          key={index}
                          className={`month-day ${!dateInfo.isCurrentMonth ? "other-month" : ""}`}
                        >
                          <div className="month-day-number">{date.format("D")}</div>
                          <div className="month-day-events">
                            {dayEvents.map((event) => (
                              <div
                                key={event.id}
                                className="month-event"
                                onClick={() => handleViewEvent(event)}
                              >
                                <span className="month-event-time">
                                  {event.start.format("h:mm A")}
                                </span>
                                <span className="month-event-title">{event.title}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                // Day/Week Time Slot View
                <div className="week-schedule">
                  <div className="time-column">
                    <div className="time-header"></div>
                    {timeSlots.map((time, index) => (
                      <div key={index} className="time-slot">
                        {time}
                      </div>
                    ))}
                  </div>

                  {getDisplayDates().map((date, index) => (
                    <div key={index} className="day-column">
                      <div className="day-header">
                        <div className="date-number">{date.format("DD")}</div>
                        <div className="day-name">{date.format("dddd")}</div>
                      </div>
                      {timeSlots.map((time, timeIndex) => {
                        // Convert time string to 24-hour format
                        let hour;
                        if (time.includes("AM")) {
                          hour = parseInt(time);
                          if (hour === 12) hour = 0; // 12 AM is 0 hours
                        } else {
                          hour = parseInt(time);
                          if (hour !== 12) hour += 12; // PM times except 12 PM
                        }
                        const dayEvents = getEventsForDateTime(date, hour);
                        const overlappingEvents = getOverlappingEvents(date, hour);
                        
                        return (
                          <div key={timeIndex} className="time-slot">
                            {dayEvents.map((event, eventIndex) => {
                              // Calculate height based on full duration (2 pixels per minute)
                              const durationMinutes = event.end.diff(event.start, "minutes");
                              const pixelHeight = durationMinutes * 1; // 2 pixels per minute (60min = 120px)
                              
                              // Find overlapping events starting at the same time for side-by-side layout
                              const sameTimeEvents = dayEvents.length;
                              const eventWidth = 100 / sameTimeEvents;
                              const leftOffset = eventIndex * eventWidth;
                              
                              return (
                                <div 
                                  key={event.id} 
                                  className="event-card"
                                  onClick={() => handleViewEvent(event)}
                                  style={{ 
                                    cursor: 'pointer',
                                    height: `${pixelHeight}px`,
                                    minHeight: `${pixelHeight}px`,
                                    maxHeight: `${pixelHeight}px`,
                                    width: sameTimeEvents > 1 ? `calc(${eventWidth}% - 8px)` : 'calc(100% - 8px)',
                                    left: sameTimeEvents > 1 ? `${leftOffset}%` : '4px'
                                  }}
                                >
                                  <div className="event-header">
                                    <div className="event-title">{event.title}</div>
                                    <div className="event-duration">
                                      {durationMinutes} mins
                                    </div>
                                  </div>
                                  <button
                                    className="view-event-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewEvent(event);
                                    }}
                                  >
                                    View Event
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Event Dialog Modal */}
      {isDialogOpen && selectedEvent && (
        <div className="dialog-overlay" onClick={closeDialog}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Event Details</h2>
              <button className="dialog-close" onClick={closeDialog}>
                ×
              </button>
            </div>
            <div className="dialog-body">
              <div className="dialog-field">
                <label>Event Name:</label>
                <p>{selectedEvent.title}</p>
              </div>
              <div className="dialog-field">
                <label>Date:</label>
                <p>{selectedEvent.start.format("MMMM DD, YYYY")}</p>
              </div>
              <div className="dialog-field">
                <label>Time:</label>
                <p>
                  {selectedEvent.start.format("h:mm A")} -{" "}
                  {selectedEvent.end.format("h:mm A")}
                </p>
              </div>
              <div className="dialog-field">
                <label>Duration:</label>
                <p>{selectedEvent.end.diff(selectedEvent.start, "minutes")} minutes</p>
              </div>
              <div className="dialog-field">
                <label>Timezone:</label>
                <p>{Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
              </div>
              <div className="dialog-field">
                <label>Type:</label>
                <p>{selectedEvent.type === "assessment" ? "Assessment Session" : "Meeting"}</p>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn-close" onClick={closeDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
